import type { FirebaseApp } from 'firebase/app';
import type { DatabaseReference } from 'firebase/database';

import type {
  AiSessionRefs,
  CustomerChatTransactionPayload,
  UserLog,
  UserSelection,
} from '../PleasedApi/models.pleased';

export type UserKind = 'customer' | 'agent';

export interface FirebaseChatMessage {
  from: UserKind;
  to: UserKind;

  userId: number;
  message: string;
  agentDisplayName?: string | null;

  attachmentFlag: boolean;
  attachmentUrl?: string;
  attachmentName?: string | null;
  attachmentId?: number | null;

  contentType?: string | null;

  userSelection?: boolean;

  createdAt: number | Date | object;
  /**
   * @internal
   */
  agentsSeen?: number[];
  /**
   * @internal
   */
  localTime?: number;
  /**
   * @internal
   */
  estimatedServerTime?: number;
  /**
   * Message kind for special message types
   */
  kind?: string;
  /**
   * Resumable chat session ID for tracking resumed chats
   */
  resumableChatSessionId?: string;
  /**
   * System message flag
   */
  systemMessage?: boolean;
  /**
   * AI message flag
   */
  isFromAI?: boolean;
}

export interface TypingMetadata {
  displayName: string;
  isAgent: boolean;
  isTyping: boolean;
}

export type TypingStore = Record<string | number, TypingMetadata>;

export interface FirebaseChatMetadataWrite {
  customerName: string;
  customerId: number;
  customerPhone: string;
}

export interface FirebaseChatMetadata extends FirebaseChatMetadataWrite {
  agentMessaged: boolean;
  typingMessage: TypingStore;
  assignedAgent: Record<string, unknown>;
  invitedAgents: number[];
}

export interface FirebaseClosedChat {
  endedAt: number;
  from: string;
  name: string;
  ticketId: string;
}

export interface FirebasePresence {
  status: 'online' | 'offline';
  chatId: number;
  customerId: number;
  lastStatusUpdateTime: number | object;
  fromDisconnect?: object | number;
  reason?: 'end-chat-local' | 'end-chat-remote' | 'heartbeat' | 'manual' | 'initial';
  /** Set when the end user explicitly clicks End Chat (agent intra-chat notification). */
  clickedEndChat?: boolean;
  clickedEndChatAt?: number | object;
}

export interface ChatMessageAttachment {
  id: string | number;
  url: string;
  contentType: string;
  name: string;
}

export interface ChatMessage {
  id: string | number;
  type: 'message.text' | 'message.file';
  message: string;

  authorType: UserKind;
  authorName: string;
  authorId: string | number;

  userSelection?: boolean;

  timestamp: number;
  /**
   * Only specified when the message type is 'message.file'
   */
  attachment?: ChatMessageAttachment;
}

export interface PleasedChatRequest {
  user: {
    id: string | number;
    displayName: string;
    email?: string;
    phone?: string;
    foreignId?: string;
    isGuest?: boolean;
  };
  subject?: string;
  ticketId?: number;
  /**
   * Raw key=value tags merged with {@link autoAddTags} and passed through the same cleaning as the legacy SDK.
   */
  chatTags?: string[];
  /**
   * Prepended to cleaned {@link chatTags} when building `systemTags` (only when `chatTags` is non-empty).
   */
  autoAddTags?: string[];
  /**
   * @deprecated Prefer `chatTags`; still supported and treated like `chatTags` when `chatTags` is omitted.
   */
  tags?: string[];
  /**
   * Same as legacy `selectedTransactions` (mapped `type` / `status` / `txId` per transaction).
   */
  transactions?: CustomerChatTransactionPayload[];
}

export interface PleasedChatRequestMetadata extends PleasedChatRequest, AiSessionRefs {
  customerAppVersion?: string;
  userSelections: UserSelection[];
}

export interface ChatMessageMetadata {
  userSelection?: boolean;
  /** Optional ms when flushing `getLiveChatQueueMessages` (overrides default server timestamp) */
  createdAt?: number;
}

export type PleasedChatCloseReason = 'local' | 'remote';

export const TYPING_ACTIVITY_THROTTLE_MS = 30_000;

export interface PleasedChatEvents {
  'chat:message': ChatMessage;
  'chat:ready': void;
  'chat:closed': { reason: PleasedChatCloseReason };
  /** Visitor activity that keeps the idle end timer from firing (message, file, typing). */
  'chat:customer:activity': void;
  'chat:queue:position': { position: number | 'clear' };
  'chat:member:participation': MemberParticipation;
  'chat:network:connected': boolean;
  // 'user:status': 'online' | 'offline';
  'user:typing': TypingStore;
  'ai:intervention': { chatId: number };
}

export type PleasedChatHandler<T extends keyof PleasedChatEvents> = (event: PleasedChatEvents[T]) => void;

export interface ChatOptions {
  user: {
    displayName: string;
    email?: string;
    phone?: string;
    foreignId?: string;
    isGuest?: boolean;
    id?: string | number;
  };
  subject?: string;
  ticketId?: number;
  chatTags?: string[];
  autoAddTags?: string[];
  tags?: string[];
  customerAppVersion?: string;
}
export interface MemberParticipation {
  type: 'member:join' | 'member:leave';
  member: { userId: string | number; timestamp: number; name: string; type: UserKind };
}
export interface ChatTypingEvent {
  userId: number;
  type: UserKind;
  isTyping: boolean;
  displayName: string;
}

export interface FirebaseSetting {
  auth: string;
  database: string;
  projectId: string;
}

export interface FirebaseDatabaseStore {
  id: string;
  app: FirebaseApp;
  messages: DatabaseReference;
  closed: DatabaseReference;
  metadata: DatabaseReference;
  typing: DatabaseReference;
  presence: DatabaseReference;

  util: {
    connection: DatabaseReference;
    timeOffset: DatabaseReference;
  };
}
